/**
 * Camera/microphone permission diagnosis and recovery guidance.
 *
 * Ported in spirit from vision-biomechanics' PermissionGate, whose central
 * assumption carries over unchanged: the operator is an untrained caregiver,
 * possibly elderly, on a phone, under time pressure. So a failure is never
 * reported as an error name — it is reported as the specific sequence of taps
 * that fixes it, in the browser they are actually holding.
 *
 * Pure functions, no React, so the decision table is unit-testable. The seed's
 * logic was tangled into a 704-line component and could not be tested at all.
 */

export type BrowserKind = 'chrome' | 'firefox' | 'safari' | 'edge' | 'other';

export type PermissionProblem =
  'denied' | 'no-device' | 'device-busy' | 'insecure-context' | 'unsupported' | 'unknown';

export function detectBrowser(userAgent: string): BrowserKind {
  // Order matters: Edge's UA contains "Chrome", and Chrome's contains "Safari".
  if (/edg/i.test(userAgent)) return 'edge';
  if (/firefox|fxios/i.test(userAgent)) return 'firefox';
  if (/chrome|chromium|crios/i.test(userAgent)) return 'chrome';
  if (/safari/i.test(userAgent)) return 'safari';
  return 'other';
}

/** Maps a getUserMedia DOMException to something we can give advice about. */
export function classifyMediaError(error: { name?: string } | null | undefined): PermissionProblem {
  switch (error?.name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
      return 'denied';
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return 'no-device';
    case 'NotReadableError':
    case 'TrackStartError':
      // Another app holds the camera. Common on phones: a video call left open.
      return 'device-busy';
    case 'SecurityError':
      return 'insecure-context';
    case 'NotSupportedError':
      return 'unsupported';
    default:
      return 'unknown';
  }
}

/**
 * getUserMedia requires a secure context. A patient link served over plain HTTP
 * fails in a way that looks like a permission denial but cannot be fixed by
 * granting permission — worth detecting separately so the advice is honest.
 */
export function isSecureContext(win: {
  isSecureContext?: boolean;
  location?: { hostname?: string };
}): boolean {
  if (win.isSecureContext) return true;
  const host = win.location?.hostname;
  return host === 'localhost' || host === '127.0.0.1';
}

export interface Recovery {
  title: string;
  steps: readonly string[];
}

/**
 * Per-browser recovery steps. These are deliberately concrete — "tap the lock
 * icon" beats "check your browser settings" when the person reading it is
 * holding a phone and does not know what a browser setting is.
 */
export function recoverySteps(problem: PermissionProblem, browser: BrowserKind): Recovery {
  if (problem === 'insecure-context') {
    return {
      title: 'This link is not secure',
      steps: [
        'Your physiotherapist needs to resend the link.',
        'The address must start with https://',
      ],
    };
  }

  if (problem === 'no-device') {
    return {
      title: 'No camera found',
      steps: [
        'Check that nothing is covering your camera.',
        'If you are on a computer, make sure a webcam is plugged in.',
        'Then tap Try again.',
      ],
    };
  }

  if (problem === 'device-busy') {
    return {
      title: 'Your camera is in use',
      steps: [
        'Close any other app that might be using the camera — video calls, or the camera app itself.',
        'Then tap Try again.',
      ],
    };
  }

  if (problem === 'unsupported') {
    return {
      title: 'This browser cannot start a video call',
      steps: [
        'Open the link in Chrome or Safari instead.',
        'Copy the link, open that browser, and paste it in.',
      ],
    };
  }

  if (problem === 'denied') {
    switch (browser) {
      case 'chrome':
        return {
          title: 'Camera and microphone are blocked',
          steps: [
            'Tap the lock icon next to the web address at the top.',
            'Tap Permissions, then turn on Camera and Microphone.',
            'Tap Try again below.',
          ],
        };
      case 'safari':
        return {
          title: 'Camera and microphone are blocked',
          steps: [
            'Tap the "aA" button on the left of the address bar.',
            'Tap Website Settings, then allow Camera and Microphone.',
            'Tap Try again below.',
          ],
        };
      case 'firefox':
        return {
          title: 'Camera and microphone are blocked',
          steps: [
            'Tap the lock icon next to the web address.',
            'Tap the blocked Camera or Microphone entry to clear it.',
            'Tap Try again below.',
          ],
        };
      case 'edge':
        return {
          title: 'Camera and microphone are blocked',
          steps: [
            'Tap the lock icon next to the web address.',
            'Tap Permissions for this site, then allow Camera and Microphone.',
            'Tap Try again below.',
          ],
        };
      default:
        return {
          title: 'Camera and microphone are blocked',
          steps: [
            'Open your browser settings for this page.',
            'Allow Camera and Microphone.',
            'Tap Try again below.',
          ],
        };
    }
  }

  return {
    title: 'We could not start your camera',
    steps: [
      'Tap Try again below.',
      'If it still does not work, close this page and reopen the link.',
    ],
  };
}
